import React from "react";
import ModalForm from "@ui/Modal/Form";
import t from "../../../../i18n";
import InputV2 from "@ui/Input/InputV2";
import useInput from "@ui/lib/hooks/useInput";
import Text from "@ui/Text";
import { styled } from "@linaria/react";
import { usePut } from "../../../../api/useCall";
import Spinner from "@ui/Spinner";
import { useTranslationTools } from "./useTranslationTools";
import type { TranslationKey } from "../../../../api/useTranslations";
import { isLeft } from "fp-ts/lib/Either";
import { short } from "diagnostics-lib/io";
import * as io from "io-ts";

const BlockTerm = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

interface P {
  onRequestClose: () => void;
  data:
    | {
        impactedElement: TranslationKey;
        frenchWord: string;
        englishWord: string;
      }
    | undefined;
  okButtonName: string;
}

const UpdateTranslationModalForm = ({ onRequestClose, data, okButtonName }: P) => {
  const { matchTranslationKey } = useTranslationTools();
  const updateTranslation = usePut("/setting/translations");

  // Input handlers
  const frenchInput = useInput(data?.frenchWord ?? "");
  const englishInput = useInput(data?.englishWord ?? "");

  // Error handlers
  const errorFrenchInput: string | undefined = React.useMemo(() => {
    if (!frenchInput.value?.length) return t("customization.modalForm.input.error.cannot_be_empty");
    if (isLeft(short(io.string).decode(frenchInput.value)))
      return t("customization.modalForm.input.error.text_to_long");
    return;
  }, [frenchInput.value]);

  const errorEnglishInput: string | undefined = React.useMemo(() => {
    if (!englishInput.value?.length)
      return t("customization.modalForm.input.error.cannot_be_empty");
    if (isLeft(short(io.string).decode(englishInput.value)))
      return t("customization.modalForm.input.error.text_to_long");
    return;
  }, [englishInput.value]);

  // Action handlers
  const dirty = React.useMemo(
    () =>
      (frenchInput.value?.length && frenchInput.value.trim() !== frenchInput.defaultValue) ||
      (englishInput.value?.length && englishInput.value.trim() !== englishInput.defaultValue),

    [frenchInput, englishInput],
  );

  const valid = React.useMemo((): boolean => {
    const noErrorOnInput =
      [errorFrenchInput].every(e => e === undefined) &&
      [errorEnglishInput].every(e => e === undefined);
    return noErrorOnInput;
  }, [errorFrenchInput, errorEnglishInput]);

  const handleSubmit = React.useCallback(async () => {
    if (!valid && !data) return false;

    if (data) {
      const updateData = {
        key: data.impactedElement,
        fr: frenchInput.value?.trim(),
        en: englishInput.value?.trim(),
      };

      await updateTranslation({
        body: [updateData],
      });
      return true;
    }
    return false;
  }, [valid, frenchInput, englishInput, data, updateTranslation]);

  if (!data) return <Spinner />;

  return (
    <ModalForm
      onSubmit={dirty && valid ? handleSubmit : false}
      onRequestClose={onRequestClose}
      okButtonName={okButtonName}
    >
      <Text type="body1" as="span">
        {t("customization.modalForm.impactedElement")}
        {": "}
        <Text type="body1SemiBold" as="span">
          {matchTranslationKey(data.impactedElement)}
        </Text>
        .
      </Text>
      <BlockTerm>
        <InputV2
          label={t("customization.modalForm.input.french")}
          error={errorFrenchInput}
          required
          autoFocus
          {...frenchInput}
        />
        <InputV2
          label={t("customization.modalForm.input.english")}
          error={errorEnglishInput}
          required
          {...englishInput}
        />
      </BlockTerm>
    </ModalForm>
  );
};

export default React.memo(UpdateTranslationModalForm);
